import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

const getAxisGridlineKey = (widgetId: string): string | null =>
  widgetId ? `axisGridlines${widgetId}` : null;

type GridlineMap = Record<string, boolean>;

interface AxisGridlineState {
  gridlines: Record<string, GridlineMap>;
}

interface AxisGridlineActions {
  loadFromStorage: (widgetId: string) => void;
  setGridline: (widgetId: string, key: string, value: boolean | null) => void;
  resetToNull: (widgetId: string) => void;
  getGridlines: (widgetId: string) => GridlineMap;
  reset: () => void;
}

const initialState: AxisGridlineState = {
  gridlines: {}
};

export const useAxisGridlineStore = create<AxisGridlineState & AxisGridlineActions>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    loadFromStorage: (widgetId) => {
      const storageKey = getAxisGridlineKey(widgetId);
      if (!storageKey) return;
      try {
        const raw = localStorage.getItem(storageKey);
        const parsed: GridlineMap = raw ? JSON.parse(raw) : {};
        set((state) => ({
          gridlines: { ...state.gridlines, [widgetId]: parsed }
        }));
      } catch {
        set((state) => ({
          gridlines: { ...state.gridlines, [widgetId]: {} }
        }));
      }
    },

    setGridline: (widgetId, key, value) => {
      const storageKey = getAxisGridlineKey(widgetId);
      if (!storageKey) return;
      set((state) => {
        const current = state.gridlines[widgetId] ?? {};
        let updated: GridlineMap;
        if (value === null) {
          updated = { ...current };
          delete updated[key];
        } else {
          updated = { ...current, [key]: value };
        }
        try {
          localStorage.setItem(storageKey, JSON.stringify(updated));
        } catch {
          // storage unavailable — continue with in-memory state only
        }
        return { gridlines: { ...state.gridlines, [widgetId]: updated } };
      });
    },

    resetToNull: (widgetId) => {
      const storageKey = getAxisGridlineKey(widgetId);
      if (storageKey) {
        try {
          localStorage.removeItem(storageKey);
        } catch {
          // storage unavailable
        }
      }
      set((state) => ({
        gridlines: { ...state.gridlines, [widgetId]: {} }
      }));
    },

    getGridlines: (widgetId) => {
      return get().gridlines[widgetId] ?? {};
    },

    reset: () => {
      Object.keys(get().gridlines).forEach((widgetId) => {
        const storageKey = getAxisGridlineKey(widgetId);
        if (storageKey) {
          try {
            localStorage.removeItem(storageKey);
          } catch {
            // storage unavailable
          }
        }
      });
      set(initialState);
    }
  }))
);
